<template>
  <section :class="['v2-profile-card', { 'v2-profile-card--collapsed': collapsed }]">
    <div class="v2-profile-card__avatar" aria-hidden="true">
      <img
        v-if="hasProfilePicture"
        :src="user.profilePicture"
        :alt="displayName"
        class="v2-profile-card__img"
      />
      <span v-else class="v2-profile-card__initials">{{ initials }}</span>
      <span v-if="!collapsed" class="v2-profile-card__status" aria-hidden="true" />
    </div>

    <div v-if="!collapsed" class="v2-profile-card__info">
      <p class="v2-profile-card__name">{{ displayName }}</p>
      <p v-if="chapterLabel" class="v2-profile-card__chapter">
         {{ chapterLabel }} {{ branchDisplayName }}
      </p>
      <span
        v-if="isSubmember"
        class="v2-profile-card__chip"
      >
        Submiembro/Colaborador
      </span>
    </div>
  </section>
</template>

<script>
import { isOrganization } from "@/share/validateOrganization";

export default {
  name: "ProfileCard",
  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isSubmember() {
      return Boolean(this.user) && this.user.role === "MEMBERSHIP_USER";
    },
    isCompanyOrganization() {
      return this.user && this.user.headOffice && this.user.headOffice.memberType === "COMPANYS";
    },
    headOfficeId() {
      if (!this.user || !this.user.headOffice) return null;
      return typeof this.user.headOffice === "object"
        ? this.user.headOffice.id || this.user.headOffice._id
        : this.user.headOffice;
    },
    isAsofom() {
      return isOrganization("asofom", null, this.headOfficeId);
    },
    chapterLabel() {
      if (!this.user || !this.user.branch) return null;
      if (this.user.branch === "N/D") return null;
      return this.isAsofom ? "Región:" : "Capítulo:";
    },
    branchDisplayName() {
      if (!this.user || !this.user.branch) return "N/D";
      return typeof this.user.branch === "object"
        ? this.user.branch.company || "N/D"
        : this.user.branch;
    },
    hasProfilePicture() {
      if (!this.user || !this.user.profilePicture) return false;
      const v = this.user.profilePicture;
      return v !== "NOT_ASSIGNED" && v !== "N/D";
    },
    displayName() {
      if (!this.user) return "N/D";
      if (this.isCompanyOrganization) return this.user.company || "N/D";
      const fullName = [this.user.name, this.user.firstLastname]
        .filter(Boolean)
        .join(" ")
        .trim();
      return fullName || "N/D";
    },
    initials() {
      const name = this.displayName || "N/D";
      return String(name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";
    },
  },
};
</script>

<style scoped>
.v2-profile-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  margin: 0 var(--space-4) var(--space-6);
  text-align: center;
  border-radius: var(--radius-lg);
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.v2-profile-card--collapsed {
  padding: 0;
  margin: 0 var(--space-2) var(--space-4);
  background: transparent;
  border: 0;
}

.v2-profile-card__avatar {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: var(--radius-full);
  background-color: rgba(255, 255, 255, 0.10);
  color: var(--color-navy-on);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  flex-shrink: 0;
  border: 2px solid rgba(255, 181, 0, 0.20);
}

.v2-profile-card--collapsed .v2-profile-card__avatar {
  border: 2px solid rgba(255, 181, 0, 0.30);
  transition: transform var(--transition-base);
}

.v2-profile-card--collapsed .v2-profile-card__avatar:hover {
  transform: scale(1.05);
}

.v2-profile-card__img {
  width: 100%;
  height: 100%;
  border-radius: var(--radius-full);
  object-fit: cover;
  display: block;
}

.v2-profile-card__initials {
  font-family: var(--font-headline);
  font-weight: var(--fw-semibold);
  font-size: var(--fs-body-md);
  color: var(--color-navy-on);
  line-height: 1;
}

.v2-profile-card__status {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: var(--radius-full);
  background-color: var(--color-success);
  border: 2px solid var(--color-navy-deep);
}

.v2-profile-card__info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  width: 100%;
}

.v2-profile-card__name {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-navy-on);
  line-height: var(--lh-snug);
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.v2-profile-card__chapter {
  margin: 0;
  font-size: var(--fs-label-xs);
  color: var(--color-navy-on-soft);
}

.v2-profile-card__chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-amber-on);
  background-color: var(--color-amber-soft);
  border-radius: var(--radius-full);
  margin-top: var(--space-1);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
}
</style>
